// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useTranslation } from "react-i18next";
import { LinearIcon } from "@/components/shared/linear-icon";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/lib/auth/auth-provider";
import { type ChangelogCategory, extractTickets } from "@/lib/changelog";
import { CHANGELOG } from "@/lib/changelog-data";
import { linearIssueUrl, useLinearLinkMode } from "@/lib/linear";
import { cn } from "@/lib/utils";

const CATEGORY_COLOR: Record<ChangelogCategory, string> = {
  Added: "text-primary",
  Changed: "text-blue-500",
  Fixed: "text-amber-500",
  Removed: "text-destructive",
  Deprecated: "text-muted-foreground",
  Security: "text-purple-500",
};

interface ChangelogDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ChangelogDialog({ open, onOpenChange }: ChangelogDialogProps) {
  const { t } = useTranslation("back-office");
  const { user } = useAuth();
  const linkMode = useLinearLinkMode();
  // Admin-group members get the version hotlinked to its release URL. Group membership
  // is a UI affordance here — back-office access itself is gated by permissions.
  const isAdmin = user?.groups.includes("admin") ?? false;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg p-0 gap-0 overflow-hidden flex flex-col">
        <div className="flex flex-col gap-4 p-6 max-h-[70vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("changelog.title")}</DialogTitle>
            <DialogDescription>{t("changelog.description")}</DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-6">
            {CHANGELOG.map((release) => {
              // Numeric versions get a "v" prefix; named sections like "Unreleased" render as-is.
              const versionLabel = /^\d/.test(release.version) ? `v${release.version}` : release.version;
              return (
                <section key={release.version} className="flex flex-col gap-3">
                  <div className="flex items-baseline gap-2 border-b pb-1.5">
                    <h3 className="text-sm font-semibold">
                      {isAdmin && release.url ? (
                        <a
                          href={release.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          {versionLabel}
                        </a>
                      ) : (
                        versionLabel
                      )}
                    </h3>
                    {release.codename && <span className="text-xs text-muted-foreground">{release.codename}</span>}
                    {release.date && (
                      <span className="ml-auto text-xs tabular-nums text-muted-foreground">{release.date}</span>
                    )}
                  </div>
                  {release.changes.map((set) => (
                    <div key={set.category} className="flex flex-col gap-1.5">
                      <span
                        className={cn("text-xs font-medium uppercase tracking-wider", CATEGORY_COLOR[set.category])}
                      >
                        {set.category}
                      </span>
                      <ul className="list-disc space-y-1 pl-5 marker:text-muted-foreground">
                        {set.items.map((item) => {
                          const { tickets, text } = extractTickets(item);
                          return (
                            <li key={item} className="text-sm text-foreground/80">
                              {tickets.map((ticket) => (
                                <a
                                  key={ticket}
                                  href={linearIssueUrl(ticket, linkMode)}
                                  target={linkMode === "web" ? "_blank" : undefined}
                                  rel={linkMode === "web" ? "noreferrer" : undefined}
                                  className="relative -top-px mr-1.5 inline-block whitespace-nowrap rounded border px-1.5 py-0.5 align-[1px] text-xs font-medium leading-none text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                                >
                                  <LinearIcon className="mr-1 inline size-3 align-middle" />
                                  <span className="relative top-px">{ticket}</span>
                                </a>
                              ))}
                              {text}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ))}
                </section>
              );
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
